import { InputWrapper } from '@ibsys/kit';
import { type ClipboardEvent, FC, type FocusEvent, Fragment, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseTimeIntervalMs } from '@/shared/lib';
import { getTimePartOrder, HOUR_MS, joinTimeParts, splitTimeMs, type TimePart, type TimeParts } from './timeParts';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import { TranscriptTimePartInput } from './TranscriptTimePartInput';
import type { TranscriptRecordFormValues } from './types';

type Props = {
  name: 'startedAt' | 'finishedAt';
  label: string;
};

/**
 * Начало или конец записи — поле времени по частям: минуты, секунды и миллисекунды, а у записи
 * разговора от часа — ещё и часы. В форму уходят миллисекунды, пока поле заполнено не целиком — null.
 */
export const TranscriptTimeInput: FC<Props> = ({ name, label }) => {
  const { t } = useTranslation(['dialogs']);
  const { control } = useFormContext<TranscriptRecordFormValues>();
  const { field, fieldState } = useController({ control, name });
  const { recordingDurationMs, timeFormat } = useTranscriptEditorContext();
  const value = field.value;

  // Время за часом показывается с часами, даже если запись разговора считается короче
  const hasHours = recordingDurationMs >= HOUR_MS || (value ?? 0) >= HOUR_MS;
  const order = getTimePartOrder(hasHours);
  const [typedParts, setTypedParts] = useState<TimeParts>(() => splitTimeMs(value, hasHours));
  const inputRefs = useRef<Partial<Record<TimePart, HTMLInputElement | null>>>({});

  // Набранное показывается, пока оно и есть значение формы (набранному не до конца соответствует null);
  // значение, заданное самой формой, раскладывается по частям заново
  const parts = joinTimeParts(typedParts, order) === value ? typedParts : splitTimeMs(value, hasHours);

  const focusPart = (part: TimePart | undefined) => part && inputRefs.current[part]?.focus();

  const handlePartChange = (part: TimePart, digits: string) => {
    const next = { ...parts, [part]: digits };
    setTypedParts(next);
    const ms = joinTimeParts(next, order);
    if (ms !== value) field.onChange(ms);
  };

  // Вставляется время целиком — в той записи, в какой оно показано в ленте, или в другой
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text');
    const ms =
      parseTimeIntervalMs(text, timeFormat) ?? parseTimeIntervalMs(text, timeFormat === 'SECOND' ? 'TIME' : 'SECOND');
    if (ms === null) return;
    setTypedParts(splitTimeMs(ms, hasHours || ms >= HOUR_MS));
    field.onChange(ms);
  };

  // Уход фокуса с поля целиком, а не переход между его частями
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) field.onBlur();
  };

  return (
    <InputWrapper label={label} error={fieldState.error?.message} required>
      <div
        role="group"
        aria-label={label}
        className="flex h-8 w-full items-center rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-within:ring-2 focus-within:ring-primary"
        onBlur={handleBlur}
      >
        {order.map((part, index) => (
          <Fragment key={part}>
            {index > 0 && <span className="select-none px-0.5">{part === 'milliseconds' ? '.' : ':'}</span>}
            <TranscriptTimePartInput
              part={part}
              value={parts[part]}
              label={t(`dialogs:transcriptEdit.timeParts.${part}`)}
              placeholder={t(`dialogs:transcriptEdit.timePartPlaceholders.${part}`)}
              inputRef={(element) => {
                inputRefs.current[part] = element;
              }}
              onChange={(digits) => handlePartChange(part, digits)}
              onPaste={handlePaste}
              onFocusPrev={() => focusPart(order[index - 1])}
              onFocusNext={() => focusPart(order[index + 1])}
            />
          </Fragment>
        ))}
      </div>
    </InputWrapper>
  );
};
